import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, useState, type JSX } from 'react'
import { toast } from 'sonner'

import { useMerchants } from '../hooks/use-merchants.ts'
import { Button } from '../design-system/button.tsx'
import { Dialog } from '../design-system/dialog.tsx'
import { Panel } from '../design-system/panel.tsx'
import {
  ALERT_RULES_QUERY_KEY,
  createAlert,
  previewAlert,
  type AlertReport,
  type NewAlert
} from '../lib/alert-rules-api.ts'
import {
  labelProblem,
  newAlertFromDraft,
  newDraft,
  triggerFromDraft,
  type AlertDraft
} from '../lib/alert-draft.ts'
import { selectedView, startingPeriod } from '../lib/alert-view.ts'
import { pagePeriod } from '../lib/page-period.ts'
import {
  ScheduleStep,
  TriggerStep,
  WatchStep,
  type SetField
} from './alert-sheet-steps.tsx'
import {
  AlertProblems,
  PreviewActions,
  PreviewStep
} from './alert-sheet-preview.tsx'

/** The four steps, in the order the sheet walks them. */
const STEPS = [
  'What to watch',
  'When to tell you',
  'How often, and how much',
  'Preview'
] as const

/** The last step's index. */
const PREVIEW_STEP = STEPS.length - 1

/**
 * AlertSheet — the sheet that builds a saved-view alert from the report on
 * screen.
 *
 * `AlertMe` finds the report in the catalogue and opens this. The sheet reads
 * the view from the URL itself, so no page hands it anything. Four steps:
 * what to watch, when to tell you, how often and how much, then a preview of
 * the DM. Save stores it and My alerts picks it up.
 *
 * @param props - Sheet props.
 * @param props.report - The catalogue entry for this page.
 * @param props.open - Whether the sheet is open.
 * @param props.onOpenChange - Opens or shuts the sheet.
 * @returns The sheet.
 */
export function AlertSheet({
  report,
  open,
  onOpenChange
}: {
  report: AlertReport
  open: boolean
  onOpenChange: (open: boolean) => void
}): JSX.Element {
  return (
    <Dialog
      data-testid="alert-sheet"
      title="Alert me"
      open={open}
      onOpenChange={onOpenChange}
    >
      <Panel
        data-testid="alert-sheet-panel"
        title="Alert me"
        description="A Slack DM or an email for you, from the view on this page."
      >
        {/* Mounted each time the sheet opens, so a second opening starts
            again from the page rather than from a half-filled draft. */}
        {open ? (
          <AlertForm report={report} onClose={() => onOpenChange(false)} />
        ) : null}
      </Panel>
    </Dialog>
  )
}

/**
 * The four steps.
 *
 * @param props - Form props.
 * @param props.report - The catalogue entry.
 * @param props.onClose - Shuts the sheet after a save.
 * @returns The step on screen.
 */
function AlertForm({
  report,
  onClose
}: {
  report: AlertReport
  onClose: () => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const directory = useMerchants()
  const search = useRouterState({ select: state => state.location.search })
  const autoPreviewed = useRef(false)
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<AlertDraft>(() =>
    newDraft(report, startingPeriod(search, report.periods, pagePeriod()))
  )
  const setField: SetField = (key, value) =>
    setDraft(current => ({ ...current, [key]: value }))
  const built = newAlertFromDraft(draft, report, search)
  const nextDisabled =
    (step === 0 &&
      selectedView(search, report, draft.period, draft.dropped) === null) ||
    (step === 1 && triggerFromDraft(draft, report) === null) ||
    (step === 2 && labelProblem(draft.label) !== null)
  const preview = useMutation({
    mutationFn: (body: NewAlert & { send?: boolean }) => previewAlert(body)
  })
  useEffect(() => {
    if (built && !autoPreviewed.current) {
      autoPreviewed.current = true
      preview.mutate(built)
    }
  }, [built, preview])
  const save = useMutation({
    mutationFn: (alert: NewAlert) => createAlert(alert),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ALERT_RULES_QUERY_KEY })
      toast.success('Alert saved')
      onClose()
    }
  })
  // The first preview runs as the sheet opens, before the reader picks where
  // the alert goes. A different choice previews again on reaching the step.
  const next = () => {
    const sentTo = preview.variables?.delivery.join()
    if (
      step + 1 === PREVIEW_STEP &&
      built &&
      sentTo !== built.delivery.join()
    ) {
      preview.mutate(built)
    }
    setStep(current => current + 1)
  }
  const failure = save.error ?? preview.error
  const stepProps = { report, draft, setField }

  return (
    <div className="grid gap-4">
      <h2 data-testid="alert-sheet-step" className="text-lg font-semibold">
        {STEPS[step]}
      </h2>

      {step === 0 ? (
        <WatchStep
          {...stepProps}
          search={search}
          merchants={directory.data?.merchants ?? []}
        />
      ) : null}
      {step === 1 ? <TriggerStep {...stepProps} /> : null}
      {step === 2 ? <ScheduleStep {...stepProps} /> : null}
      {step === PREVIEW_STEP ? (
        <PreviewStep
          preview={preview.data}
          delivery={preview.variables?.delivery ?? ['slack']}
        />
      ) : null}

      {failure ? <AlertProblems error={failure} /> : null}

      <div className="flex flex-wrap gap-2">
        {step > 0 ? (
          <Button
            data-testid="alert-sheet-back"
            variant="outline"
            onClick={() => setStep(current => current - 1)}
          >
            Back
          </Button>
        ) : null}
        {step < PREVIEW_STEP ? (
          <Button
            data-testid="alert-sheet-next"
            disabled={nextDisabled}
            onClick={next}
          >
            Next
          </Button>
        ) : null}
        {step === PREVIEW_STEP && built ? (
          <PreviewActions alert={built} preview={preview} save={save} />
        ) : null}
      </div>
    </div>
  )
}
